const SVG_NS = 'http://www.w3.org/2000/svg'
const MARGIN = { top: 10, right: 20, bottom: 30, left: 55 }
const POWER_HEIGHT = 200
const ROW_HEIGHT = 28
const ROW_GAP = 12
const TICK_COUNT = 6

function el(name, attrs = {}, text) {
  const node = document.createElementNS(SVG_NS, name)
  Object.entries(attrs).forEach(([k, v]) => {
    if (v != null) node.setAttribute(k, v)
  })
  if (text != null) node.textContent = text
  return node
}

const pointsBySource = new Map()

function parsePoints(container) {
  const sourceSel = container.dataset.source
  if (sourceSel) {
    if (pointsBySource.has(sourceSel)) return pointsBySource.get(sourceSel)
    const sourceEl = document.querySelector(sourceSel)
    const raw = sourceEl ? sourceEl.textContent : ''
    const parsed = raw
      ? JSON.parse(raw).map((p) => ({ ...p, t: new Date(p.t).getTime() }))
      : []
    pointsBySource.set(sourceSel, parsed)
    return parsed
  }
  const raw = container.dataset.points
  if (!raw) return []
  return JSON.parse(raw).map((p) => ({
    ...p,
    t: new Date(p.t).getTime(),
  }))
}

function pad(n) {
  return n < 10 ? '0' + n : String(n)
}

function formatTime(ts) {
  const d = new Date(ts)
  return pad(d.getHours()) + ':' + pad(d.getMinutes())
}

function formatTimestamp(ts) {
  const d = new Date(ts)
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    ' ' +
    pad(d.getHours()) +
    ':' +
    pad(d.getMinutes()) +
    ':' +
    pad(d.getSeconds())
  )
}

function ensureTooltip(container) {
  if (container.style.position !== 'absolute') container.style.position = 'relative'
  let tip = container.querySelector('.device-timechart-tooltip')
  if (tip) return tip
  tip = document.createElement('div')
  tip.className = 'device-timechart-tooltip'
  Object.assign(tip.style, {
    position: 'absolute',
    pointerEvents: 'none',
    background: 'rgba(33, 37, 41, 0.92)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    transform: 'translate(-50%, calc(-100% - 6px))',
    display: 'none',
    zIndex: '10',
  })
  container.appendChild(tip)
  return tip
}

function attachDotTooltip(hitNode, container, label) {
  const tip = ensureTooltip(container)
  hitNode.style.cursor = 'pointer'
  hitNode.addEventListener('mouseenter', () => {
    tip.innerHTML = label
    const r = hitNode.getBoundingClientRect()
    const c = container.getBoundingClientRect()
    tip.style.left = r.left - c.left + r.width / 2 + 'px'
    tip.style.top = r.top - c.top + 'px'
    tip.style.display = 'block'
  })
  hitNode.addEventListener('mouseleave', () => {
    tip.style.display = 'none'
  })
}

function makeTimeScale(domain, range) {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0 || 1
  return (t) => r0 + ((t - d0) / span) * (r1 - r0)
}

function makeLinearScale(domain, range) {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0 || 1
  return (v) => r0 + ((v - d0) / span) * (r1 - r0)
}

function timeTicks(domain, count) {
  const [d0, d1] = domain
  const step = (d1 - d0) / count
  const ticks = []
  for (let i = 0; i <= count; i += 1) {
    ticks.push(d0 + step * i)
  }
  return ticks
}

function buildSvg(container, height) {
  const width = container.clientWidth || 600
  const svg = el('svg', { width, height })
  container.appendChild(svg)
  return { svg, width, height }
}

function drawAxisBottom(parent, x, domain, y, width) {
  const axisGroup = el('g', { transform: `translate(0,${y})` })
  axisGroup.appendChild(el('line', { x1: 0, x2: width, y1: 0, y2: 0, stroke: 'currentColor' }))
  timeTicks(domain, TICK_COUNT).forEach((t) => {
    const tx = x(t)
    axisGroup.appendChild(el('line', { x1: tx, x2: tx, y1: 0, y2: 4, stroke: 'currentColor' }))
    axisGroup.appendChild(
      el(
        'text',
        { x: tx, y: 16, 'text-anchor': 'middle', 'font-size': 10, fill: 'currentColor' },
        formatTime(t)
      )
    )
  })
  parent.appendChild(axisGroup)
}

function drawAxisLeft(parent, y, domain, height, suffix = '') {
  const axisGroup = el('g')
  axisGroup.appendChild(el('line', { x1: 0, x2: 0, y1: 0, y2: height, stroke: 'currentColor' }))
  const [d0, d1] = domain
  const ticks = 5
  const step = (d1 - d0) / ticks
  const decimals = step >= 1 ? 0 : 1
  for (let i = 0; i <= ticks; i += 1) {
    const v = d0 + step * i
    const ty = y(v)
    axisGroup.appendChild(el('line', { x1: -4, x2: 0, y1: ty, y2: ty, stroke: 'currentColor' }))
    axisGroup.appendChild(
      el(
        'text',
        { x: -8, y: ty + 3, 'text-anchor': 'end', 'font-size': 10, fill: 'currentColor' },
        v.toFixed(decimals) + suffix
      )
    )
  }
  parent.appendChild(axisGroup)
}

function niceDomain(values) {
  if (values.length === 0) return [0, 1]
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.1, 1)
    return [min - pad, max + pad]
  }
  const pad = (max - min) * 0.1
  return [min - pad, max + pad]
}

function timeDomain(container) {
  const to = Date.parse(container.dataset.to) || Date.now()
  const from = Date.parse(container.dataset.from) || to - 4 * 3600 * 1000
  return [from, to]
}

function buildSegments(points, key, endTime) {
  const segments = []
  for (let i = 0; i < points.length; i += 1) {
    const next = points[i + 1]
    segments.push({
      start: points[i].t,
      end: next ? next.t : endTime,
      value: points[i][key],
    })
  }
  return segments
}

function renderPower(container, points) {
  const { svg, width } = buildSvg(container, POWER_HEIGHT)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = POWER_HEIGHT - MARGIN.top - MARGIN.bottom

  const domain = timeDomain(container)
  const x = makeTimeScale(domain, [0, innerWidth])
  const y = makeLinearScale([0, 100], [innerHeight, 0])

  const g = el('g', { transform: `translate(${MARGIN.left},${MARGIN.top})` })
  svg.appendChild(g)

  buildSegments(points, 'on_battery', domain[1]).forEach((seg) => {
    g.appendChild(
      el('rect', {
        x: x(seg.start),
        y: 0,
        width: Math.max(0, x(seg.end) - x(seg.start)),
        height: innerHeight,
        fill: seg.value ? '#fff3cd' : '#d1e7dd',
        opacity: 0.5,
      })
    )
  })

  drawAxisBottom(g, x, domain, innerHeight, innerWidth)
  drawAxisLeft(g, y, [0, 100], innerHeight, '%')

  const valid = points.filter((p) => p.battery_percentage != null)
  if (valid.length > 1) {
    const d = valid
      .map((p, i) => (i === 0 ? 'M' : 'L') + x(p.t) + ',' + y(p.battery_percentage))
      .join(' ')
    g.appendChild(el('path', { d, fill: 'none', stroke: '#0d6efd', 'stroke-width': 2 }))
  }

  valid.forEach((p) => {
    const cx = x(p.t)
    const cy = y(p.battery_percentage)
    g.appendChild(
      el('circle', {
        cx,
        cy,
        r: 2,
        fill: p.on_battery ? '#fd7e14' : '#198754',
      })
    )
    const hit = el('circle', { cx, cy, r: 8, fill: 'transparent' })
    g.appendChild(hit)
    const label =
      formatTimestamp(p.t) +
      '<br><strong>' +
      p.battery_percentage +
      '%</strong> · ' +
      (p.on_battery ? 'on battery' : 'plugged in')
    attachDotTooltip(hit, container, label)
  })

  const legend = el('g', { transform: `translate(${MARGIN.left},${POWER_HEIGHT - 8})` })
  ;[
    { color: '#198754', label: 'Plugged in' },
    { color: '#fd7e14', label: 'On battery' },
  ].forEach((item, i) => {
    const offset = i * 100
    legend.appendChild(el('circle', { cx: offset, cy: 0, r: 4, fill: item.color }))
    legend.appendChild(
      el(
        'text',
        { x: offset + 8, y: 4, 'font-size': 10, fill: 'currentColor' },
        item.label
      )
    )
  })
  svg.appendChild(legend)
}

function renderLine(container, points, options) {
  const { key, color, suffix = '', decimals = 1, height = 180 } = options
  const { svg, width } = buildSvg(container, height)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = height - MARGIN.top - MARGIN.bottom

  const domain = timeDomain(container)
  const x = makeTimeScale(domain, [0, innerWidth])

  const valid = points.filter((p) => p[key] != null)
  const yDomain = niceDomain(valid.map((p) => p[key]))
  const y = makeLinearScale(yDomain, [innerHeight, 0])

  const g = el('g', { transform: `translate(${MARGIN.left},${MARGIN.top})` })
  svg.appendChild(g)

  drawAxisBottom(g, x, domain, innerHeight, innerWidth)
  drawAxisLeft(g, y, yDomain, innerHeight, suffix)

  if (valid.length > 1) {
    const d = valid
      .map((p, i) => (i === 0 ? 'M' : 'L') + x(p.t) + ',' + y(p[key]))
      .join(' ')
    g.appendChild(el('path', { d, fill: 'none', stroke: color, 'stroke-width': 2 }))
  }

  valid.forEach((p) => {
    const cx = x(p.t)
    const cy = y(p[key])
    g.appendChild(el('circle', { cx, cy, r: 2, fill: color }))
    const hit = el('circle', { cx, cy, r: 8, fill: 'transparent' })
    g.appendChild(hit)
    const value = Number(p[key]).toFixed(decimals) + suffix
    attachDotTooltip(hit, container, formatTimestamp(p.t) + '<br><strong>' + value + '</strong>')
  })
}

const DRIFT_OK_MS = 500
const DRIFT_WARN_MS = 2000

function driftColor(value) {
  const abs = Math.abs(value)
  if (abs < DRIFT_OK_MS) return '#198754'
  if (abs < DRIFT_WARN_MS) return '#fd7e14'
  return '#dc3545'
}

function formatDrift(value) {
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  const abs = Math.abs(value)
  return abs < 1000 ? sign + Math.round(abs) + ' ms' : sign + (abs / 1000).toFixed(1) + ' s'
}

function driftExtent(values) {
  const maxAbs = Math.max(DRIFT_OK_MS * 1.5, ...values.map((v) => Math.abs(v))) * 1.05
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxAbs)))
  const step = [1, 1.5, 2, 3, 4, 6, 8, 10].find((m) => m * magnitude >= maxAbs)
  return step * magnitude
}

function drawDriftBands(g, y, yDomain, innerWidth) {
  const [lo, hi] = yDomain
  const clamp = (v) => Math.min(hi, Math.max(lo, v))
  ;[
    { from: DRIFT_OK_MS, to: DRIFT_WARN_MS, fill: '#fff3cd' },
    { from: DRIFT_WARN_MS, to: Infinity, fill: '#f8d7da' },
  ].forEach((band) => {
    ;[1, -1].forEach((sign) => {
      const a = y(clamp(sign * band.from))
      const b = y(clamp(sign * band.to))
      const top = Math.min(a, b)
      const height = Math.abs(a - b)
      if (height <= 0) return
      g.appendChild(el('rect', { x: 0, y: top, width: innerWidth, height, fill: band.fill, opacity: 0.6 }))
    })
  })
  g.appendChild(
    el('line', {
      x1: 0,
      x2: innerWidth,
      y1: y(0),
      y2: y(0),
      stroke: 'currentColor',
      'stroke-dasharray': '4 4',
      opacity: 0.5,
    })
  )
}

function drawDriftAxisLeft(parent, y, extent, height) {
  const axisGroup = el('g')
  axisGroup.appendChild(el('line', { x1: 0, x2: 0, y1: 0, y2: height, stroke: 'currentColor' }))
  ;[-1, -0.5, 0, 0.5, 1].forEach((k) => {
    const v = k * extent
    const ty = y(v)
    axisGroup.appendChild(el('line', { x1: -4, x2: 0, y1: ty, y2: ty, stroke: 'currentColor' }))
    axisGroup.appendChild(
      el(
        'text',
        { x: -8, y: ty + 3, 'text-anchor': 'end', 'font-size': 10, fill: 'currentColor' },
        formatDrift(v)
      )
    )
  })
  parent.appendChild(axisGroup)
}

function renderDrift(container, points) {
  const height = 180
  const { svg, width } = buildSvg(container, height)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = height - MARGIN.top - MARGIN.bottom

  const domain = timeDomain(container)
  const x = makeTimeScale(domain, [0, innerWidth])

  const valid = points.filter((p) => p.clock_drift_ms != null)
  const extent = driftExtent(valid.map((p) => p.clock_drift_ms))
  const yDomain = [-extent, extent]
  const y = makeLinearScale(yDomain, [innerHeight, 0])

  const g = el('g', { transform: `translate(${MARGIN.left},${MARGIN.top})` })
  svg.appendChild(g)

  drawDriftBands(g, y, yDomain, innerWidth)
  drawAxisBottom(g, x, domain, innerHeight, innerWidth)
  drawDriftAxisLeft(g, y, extent, innerHeight)

  let d = ''
  let open = false
  points.forEach((p) => {
    if (p.clock_drift_ms == null) {
      open = false
      return
    }
    d += (open ? ' L' : ' M') + x(p.t) + ',' + y(p.clock_drift_ms)
    open = true
  })
  if (d) g.appendChild(el('path', { d: d.trim(), fill: 'none', stroke: '#0d6efd', 'stroke-width': 2 }))

  valid.forEach((p) => {
    const cx = x(p.t)
    const cy = y(p.clock_drift_ms)
    g.appendChild(el('circle', { cx, cy, r: 2, fill: driftColor(p.clock_drift_ms) }))
    const hit = el('circle', { cx, cy, r: 8, fill: 'transparent' })
    g.appendChild(hit)
    attachDotTooltip(
      hit,
      container,
      formatTimestamp(p.t) + '<br><strong>' + formatDrift(p.clock_drift_ms) + '</strong>'
    )
  })
}

const MINUTE_MS = 60 * 1000

function buildMinutePresenceSegments(points, domain) {
  const [start, end] = domain
  const startMinute = Math.floor(start / MINUTE_MS)
  const endMinute = Math.floor(end / MINUTE_MS)
  const totalMinutes = endMinute - startMinute + 1
  if (totalMinutes <= 0) return []

  const present = new Uint8Array(totalMinutes)
  for (let i = 0; i < points.length; i += 1) {
    const idx = Math.floor(points[i].t / MINUTE_MS) - startMinute
    if (idx >= 0 && idx < totalMinutes) present[idx] = 1
  }

  const segments = []
  let runStart = 0
  for (let i = 1; i <= totalMinutes; i += 1) {
    if (i === totalMinutes || present[i] !== present[runStart]) {
      segments.push({
        start: (startMinute + runStart) * MINUTE_MS,
        end: (startMinute + i) * MINUTE_MS,
        online: present[runStart] === 1,
      })
      runStart = i
    }
  }
  return segments
}

function renderConnectivity(container, points) {
  const height = MARGIN.top + MARGIN.bottom + ROW_HEIGHT
  const { svg, width } = buildSvg(container, height)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = ROW_HEIGHT

  const domain = timeDomain(container)
  const x = makeTimeScale(domain, [0, innerWidth])

  const g = el('g', { transform: `translate(${MARGIN.left},${MARGIN.top})` })
  svg.appendChild(g)

  g.appendChild(
    el('rect', {
      x: 0,
      y: 0,
      width: innerWidth,
      height: ROW_HEIGHT,
      fill: '#dee2e6',
      opacity: 0.3,
    })
  )

  buildMinutePresenceSegments(points, domain).forEach((seg) => {
    const sx = Math.max(0, x(seg.start))
    const ex = Math.min(innerWidth, x(seg.end))
    g.appendChild(
      el('rect', {
        x: sx,
        y: 0,
        width: Math.max(0, ex - sx),
        height: ROW_HEIGHT,
        fill: seg.online ? '#198754' : '#dc3545',
      })
    )
  })

  g.appendChild(
    el(
      'text',
      {
        x: -8,
        y: ROW_HEIGHT / 2 + 4,
        'text-anchor': 'end',
        'font-size': 11,
        'font-weight': 'bold',
        fill: 'currentColor',
      },
      'Online'
    )
  )

  drawAxisBottom(g, x, domain, innerHeight, innerWidth)
}

const MIN_BRUSH_PX = 4

function visit(href) {
  if (window.Turbo) window.Turbo.visit(href)
  else window.location.assign(href)
}

function attachZoomBrush(container) {
  const svg = container.querySelector('svg')
  const url = container.dataset.url
  if (!svg || !url) return

  const width = Number(svg.getAttribute('width'))
  const height = Number(svg.getAttribute('height'))
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = height - MARGIN.top - MARGIN.bottom
  const domain = timeDomain(container)
  const toTime = (px) => domain[0] + (px / innerWidth) * (domain[1] - domain[0])
  const clampX = (px) => Math.min(innerWidth, Math.max(0, px))

  const layer = el('g', { transform: `translate(${MARGIN.left},${MARGIN.top})` })
  const selection = el('rect', {
    y: 0,
    height: innerHeight,
    fill: '#0d6efd',
    opacity: 0.15,
    stroke: '#0d6efd',
    'stroke-width': 1,
    display: 'none',
  })
  layer.appendChild(selection)
  svg.appendChild(layer)
  svg.style.cursor = 'crosshair'
  svg.style.userSelect = 'none'
  svg.style.touchAction = 'none'

  let startX = null

  const localX = (event) => clampX(event.clientX - svg.getBoundingClientRect().left - MARGIN.left)

  svg.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return
    event.preventDefault()
    startX = localX(event)
    svg.setPointerCapture(event.pointerId)
  })

  svg.addEventListener('pointermove', (event) => {
    if (startX == null) return
    const currentX = localX(event)
    selection.setAttribute('x', Math.min(startX, currentX))
    selection.setAttribute('width', Math.abs(currentX - startX))
    selection.removeAttribute('display')
  })

  const finish = (event) => {
    if (startX == null) return
    const endX = localX(event)
    const from = Math.min(startX, endX)
    const to = Math.max(startX, endX)
    startX = null
    selection.setAttribute('display', 'none')
    if (to - from < MIN_BRUSH_PX) return

    const params = new URLSearchParams({
      from: new Date(toTime(from)).toISOString(),
      to: new Date(toTime(to)).toISOString(),
    })
    visit(url + '?' + params.toString())
  }

  svg.addEventListener('pointerup', finish)
  svg.addEventListener('pointercancel', () => {
    startX = null
    selection.setAttribute('display', 'none')
  })
}

function renderChart(container) {
  container.innerHTML = ''
  const points = parsePoints(container)
  const kind = container.dataset.chart

  if (kind === 'power') renderPower(container, points)
  else if (kind === 'connectivity') renderConnectivity(container, points)
  else if (kind === 'temperature') {
    renderLine(container, points, {
      key: 'cpu_temperature',
      color: '#dc3545',
      suffix: '°',
    })
  } else if (kind === 'load') {
    renderLine(container, points, {
      key: 'load_average',
      color: '#6610f2',
      decimals: 2,
    })
  } else if (kind === 'drift') renderDrift(container, points)

  attachZoomBrush(container)
}

export default function setupDeviceTimecharts() {
  pointsBySource.clear()
  document.querySelectorAll('.device-timechart').forEach(renderChart)
}
